import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { UserService } from '@app/user/services';
import { PATHS } from '@shared/constants/paths';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

/**
 * Functional guard for Service Level Reports access.
 * Validates that the user has permission to access service level reports
 * based on their user profile configuration (CCA).
 *
 * Waits for user profiles to be loaded before making the access decision
 * to prevent premature redirects during page reload.
 *
 * @param redirectPath Path to redirect to if access is denied. Default: home
 * @returns true if user has access, or UrlTree for redirect path
 */
export function serviceLevelGuard(
  redirectPath: string = PATHS.HOME,
): CanActivateFn {
  return (): Observable<boolean | UrlTree> => {
    const userService = inject(UserService);
    const router = inject(Router);

    return combineLatest([
      userService.showServiceLevelReports$(),
      userService.userProfilesState$(),
    ]).pipe(
      filter(([_hasAccess, userProfilesState]) => !!userProfilesState?.loaded),
      take(1),
      map(([hasAccess, _userProfilesState]) => {
        return hasAccess ? true : router.parseUrl(redirectPath);
      }),
    );
  };
}
